import { useEffect, useMemo, useState } from "react";
import { GetASD } from "../../CreditoyCobranza/ReporteCobranza/Actions/GetContainer";

// Capa 2: carga Región/Sucursal/Departamento (FUtiliti/GetAdrSucursalDepartamento)
// una sola vez por sesión y resuelve la cascada Región → Sucursal → Departamento
// sin mutar arreglos. Selección vacía = "todas".
// Fecha de creación: 2026-09-15 — Autor: IA (Claude)
let cacheASD = null;
const obtenerASD = () => {
  if (!cacheASD) {
    cacheASD = GetASD()
      .then((res) => (Array.isArray(res) ? res : []))
      .catch((error) => {
        cacheASD = null; // permite reintentar en el siguiente montaje
        throw error;
      });
  }
  return cacheASD;
};

const unicos = (filas, campoId, campoLabel) => {
  const mapa = new Map();
  filas.forEach((fila) => {
    const id = fila[campoId];
    if (id === null || id === undefined || mapa.has(String(id))) return;
    mapa.set(String(id), { id: String(id), label: fila[campoLabel] ?? "" });
  });
  return [...mapa.values()];
};

const aTexto = (ids) => (Array.isArray(ids) ? ids.map(String) : []);

export const useFiltroASD = ({ activo = false, sucursalesPermitidas, inicial = {}, regionesMarcadasPorDefecto = true } = {}) => {
  const [data, setData] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(false);
  const [seleccion, setSeleccion] = useState({
    adr: aTexto(inicial.adr),
    sucursal: aTexto(inicial.sucursal),
    departamento: aTexto(inicial.departamento),
  });
  const [inicializado, setInicializado] = useState(Array.isArray(inicial.adr));

  useEffect(() => {
    if (!activo) return;
    let vigente = true;
    setCargando(true);
    obtenerASD()
      .then((res) => vigente && setData(res))
      .catch(() => vigente && setError(true))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [activo]);

  const permitidas = useMemo(
    () => (Array.isArray(sucursalesPermitidas) ? new Set(sucursalesPermitidas.map((s) => String(s.idsucursal ?? s))) : null),
    [sucursalesPermitidas]
  );

  const base = useMemo(
    () => (permitidas ? data.filter((f) => permitidas.has(String(f.idsucursal))) : data),
    [data, permitidas]
  );

  const regiones = useMemo(() => unicos(base, "idadr", "adr"), [base]);

  // Primera carga: marca todas las regiones (mismo comportamiento que el Dashboard de Cobranza).
  useEffect(() => {
    if (inicializado || regiones.length === 0) return;
    if (regionesMarcadasPorDefecto) setSeleccion((prev) => ({ ...prev, adr: regiones.map((r) => r.id) }));
    setInicializado(true);
  }, [regiones, inicializado, regionesMarcadasPorDefecto]);

  const sucursales = useMemo(() => {
    const filas = seleccion.adr.length ? base.filter((f) => seleccion.adr.includes(String(f.idadr))) : base;
    return unicos(filas, "idsucursal", "sucursal");
  }, [base, seleccion.adr]);

  const departamentos = useMemo(() => {
    let filas = seleccion.adr.length ? base.filter((f) => seleccion.adr.includes(String(f.idadr))) : base;
    if (seleccion.sucursal.length) filas = filas.filter((f) => seleccion.sucursal.includes(String(f.idsucursal)));
    return unicos(filas, "iddepartamento", "departamento");
  }, [base, seleccion.adr, seleccion.sucursal]);

  // Al cambiar un nivel, se descartan selecciones hijas que ya no aplican.
  const toggle = (nivel, id) => {
    setSeleccion((prev) => {
      const actual = prev[nivel];
      const siguiente = actual.includes(id) ? actual.filter((x) => x !== id) : [...actual, id];
      const nuevo = { ...prev, [nivel]: siguiente };
      if (nivel === "adr") {
        const validas = new Set(
          (siguiente.length ? base.filter((f) => siguiente.includes(String(f.idadr))) : base).map((f) => String(f.idsucursal))
        );
        nuevo.sucursal = prev.sucursal.filter((s) => validas.has(s));
      }
      if (nivel !== "departamento") {
        let filas = nuevo.adr.length ? base.filter((f) => nuevo.adr.includes(String(f.idadr))) : base;
        if (nuevo.sucursal.length) filas = filas.filter((f) => nuevo.sucursal.includes(String(f.idsucursal)));
        const validos = new Set(filas.map((f) => String(f.iddepartamento)));
        nuevo.departamento = prev.departamento.filter((d) => validos.has(d));
      }
      return nuevo;
    });
  };

  const limpiar = () =>
    setSeleccion({ adr: regionesMarcadasPorDefecto ? regiones.map((r) => r.id) : [], sucursal: [], departamento: [] });

  return { regiones, sucursales, departamentos, seleccion, toggle, limpiar, cargando, error };
};
